import { Alert } from '@/global/components/Alert'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import Skeleton from '@/global/components/Skeleton'
import Grid from '@/global/components/ui/Grid'
import ContentGuide from '@/global/guides/ContentGuide'
import {
    formatarHoras,
    formatPorcentagemRegra,
    onlyDefined,
} from '@acme/common'
import { SelectFuncionario } from 'global/components/Selects'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { funcionario as funcionario_api, regrasCalculo } from 'service/api'
import { useStore } from 'store'

interface TipoHora {
    tipo: string
    quantidade: string
}

const FALTAS_KEY = 'Faltas'

export default function FormImplantacao() {
    const { setValue, watch } = useFormContext()
    const { data: regras } = regrasCalculo.useFetch()
    const funcionarioId = useStore(state => state.employeeId)
    const { data: funcionario } = funcionario_api.useFetchById(
        Number(funcionarioId),
    )
    const [porcentagens, faltas] = watch(['porcentagemHorasExtras', 'faltas'])
    const [tipos, setTipos] = useState<TipoHora[]>()

    useEffect(() => {
        if (!regras || !funcionario) return
        else {
            const opcoes =
                regras
                    ?.find(r => r.id == funcionario?.regraCalculo)
                    ?.porcentagens.map(p => p.porcentagem?.toString())
                    .filter(onlyDefined) ?? []

            const defaultValues = Object.entries(porcentagens ?? {})
                .map(([key, value]) => ({
                    tipo: key,
                    quantidade: formatarHoras(value as any, 3),
                }))
                .concat({
                    tipo: FALTAS_KEY,
                    quantidade: formatarHoras(faltas, 3),
                })

            setTipos(
                [
                    ...opcoes
                        .map(v => ({
                            tipo: v,
                            quantidade: '000:00',
                        }))
                        .filter(
                            v => !defaultValues.some(dv => dv.tipo === v.tipo),
                        ),
                    ...defaultValues,
                ].sort((a, b) => a.tipo.localeCompare(b.tipo)),
            )
        }
    }, [regras, funcionario?.id])

    useEffect(() => {
        setValue(
            'valores',
            tipos?.reduce((acc, curr) => {
                return {
                    ...acc,
                    [curr.tipo]: curr.quantidade,
                }
            }, {}),
        )
    }, [tipos])

    if (!funcionario) return <Loading />

    return (
        <Grid container>
            <Grid item xs={12}>
                <Alert severity="warning">
                    Caso a sua regra de cálculo possua compensação, recomenda-se
                    implantar o saldo de horas extras já compensando as faltas.
                </Alert>
            </Grid>
            <Grid item xs={12}>
                <SelectFuncionario variant="form" />
            </Grid>
            <Grid item xs={12}>
                <ContentGuide.DataGrid
                    loading={false}
                    ToolbarProps={{ hideToolbar: true }}
                    hideFooter
                    columns={[
                        {
                            ...optionsColumn,
                            field: 'tipo',
                            headerName: '',
                            width: 200,
                            valueFormatter: ({ value }) =>
                                value === FALTAS_KEY
                                    ? 'Faltas'
                                    : `Horas extras ${formatPorcentagemRegra(
                                          value,
                                      )}`,
                        },
                        {
                            ...optionsColumn,
                            field: 'quantidade',
                            headerName: 'Saldo para implantar',
                            flex: 1,
                            editable: true,
                            headerAlign: 'center',
                            renderHeader: () => (
                                <span
                                    className="inline-flex items-center justify-between gap-6"
                                    onClick={e => e.stopPropagation()}
                                >
                                    Saldo para implantar
                                </span>
                            ),
                        },
                    ]}
                    rows={
                        tipos?.map((v, i) => ({
                            ...v,
                            id: i,
                        })) || []
                    }
                    headerHeight={36}
                    rowHeight={35}
                    onCellEditCommit={({ id, field, value: rawValue = '' }) => {
                        let value = String(rawValue).trim()
                        if (field !== 'quantidade') return
                        if (!/^\d{1,3}:\d{2}$/.test(value)) {
                            if (value.includes(':')) {
                                const [hr, min] = value.split(':')
                                value = `${hr.padStart(3, '0')}:${min.padEnd(
                                    2,
                                    '0',
                                )}`
                            } else value = `${value.padStart(3, '0')}:00`
                        }

                        const clone =
                            tipos?.map((v, i) =>
                                i === id ? { ...v, [field]: value } : v,
                            ) ?? []

                        const porcentagensClone = clone.filter(
                            it => it.tipo !== FALTAS_KEY,
                        )

                        const porcentagens = porcentagensClone.reduce(
                            (acc, curr) =>
                                curr.tipo === FALTAS_KEY
                                    ? acc
                                    : {
                                          ...acc,
                                          [curr.tipo]: formatarHoras(
                                              curr.quantidade,
                                              3,
                                          ),
                                      },
                            {},
                        )

                        setValue('porcentagemHorasExtras', porcentagens)
                        setValue(
                            'faltas',
                            clone.find(v => v.tipo === FALTAS_KEY)?.quantidade,
                        )
                        setTipos(clone)
                    }}
                    style={{ minHeight: 140 }}
                />
            </Grid>
        </Grid>
    )
}

function Loading() {
    return (
        <div className="flex flex-col flex-grow gap-4">
            <Skeleton className="h-[80px]" />
            <Skeleton className="h-[150px]" />
        </div>
    )
}
